import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiMoreHorizontal, FiMoreVertical, FiPlus, FiTrash2 } from "react-icons/fi";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import {
  assignItemsToChoice,
  hiddenChoiceItemsFromKeep,
  removeItemFromChoice,
  syncProductChoicesAndItems,
} from "../../../services/MenuService";
import { choiceTitle, itemTitle } from "../Cms/MenuTree/menuTreeUtils";
import AddChoice from "./AddChoice";
import EditChoice from "./EditChoice";
import AddItem from "./AddItem";
import EditItem from "./EditItem";
import DualPriceFields, { ExtraSizeDualPriceInputs } from "./DualPriceFields";
import { parseOptionalPrice } from "../../../utils/currency";
import NutritionValuesEditor from "../shared/NutritionValuesEditor";
import ProductBadgesEditor from "../shared/ProductBadgesEditor";
import { normalizeNutritionValuesForSave } from "../../../constants/nutritionOptions";
import { normalizeProductBadgesForSave } from "../../../constants/productBadgeOptions";
import { IMAGE_PICKER_ACCEPT, validateImageFile } from "../../../utils/imageValidation";
import "../../../i18n";
import "./AddCategory.css";
import "../shared/ProductExtras.css";

const photoUrl = (photo) => `${config.UPLOADS_SERVER}/product/${photo}`;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
});
const asId = (value) => {
  if (value == null) return "";
  if (typeof value === "object" && value._id != null) return String(value._id);
  return String(value);
};

const ProductEditing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const languages = useMemo(() => getMenuLanguages(t), [t]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [productDetails, setProductDetails] = useState(() =>
    Object.fromEntries(
      MENU_LANGUAGE_CODES.map((code) => [code, { productTitle: "", productDescription: "" }])
    )
  );
  const [openLangs, setOpenLangs] = useState({});
  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));

  const [formData, setFormData] = useState({
    price: 0,
    secondaryPrice: "",
    isMultipleSize: false,
    extraSizes: [{ size: "", finalPrice: "", secondaryFinalPrice: "" }],
  });
  const [imageDisabled, setImageDisabled] = useState(false);
  const [productBadges, setProductBadges] = useState([]);
  const [nutritionValues, setNutritionValues] = useState([]);
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");

  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [savedPhotos, setSavedPhotos] = useState([]);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const previewSrc =
    imageUrl || (!photoRemoved && savedPhotos[0] ? photoUrl(savedPhotos[0]) : null);

  const [choices, setChoices] = useState([]);
  const [choicesLoading, setChoicesLoading] = useState(true);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [selectedItemsByChoice, setSelectedItemsByChoice] = useState({});
  const [choiceItemsMap, setChoiceItemsMap] = useState({});
  const [hiddenByChoice, setHiddenByChoice] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [choicesOpen, setChoicesOpen] = useState(false);
  const [openChoiceBodies, setOpenChoiceBodies] = useState({});

  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchItemTerm, setSearchItemTerm] = useState("");
  const [filteredItems, setfilteredItems] = useState([]);
  const [activeSection, setActiveSection] = useState("existing");
  const [openChoiceMenuId, setOpenChoiceMenuId] = useState(null);
  const [openItemMenuId, setOpenItemMenuId] = useState(null);
  const [showAddChoiceModal, setShowAddChoiceModal] = useState(false);
  const [showEditChoiceModal, setShowEditChoiceModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingChoice, setEditingChoice] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const fetchChoices = async (search) => {
    const params = new URLSearchParams();
    if (search && search.trim()) params.set("search", search.trim());
    const url = `${config.dev}/v2/choices/retrieve${params.toString() ? `?${params}` : ""}`;
    const response = await axios.get(url, { headers: authHeaders() });
    const data = (response?.data || []).filter((choice) => !choice.isArchived);
    setChoices(data);
    return data;
  };

  const fetchChoiceItemsForChoice = async (choiceId) => {
    try {
      const response = await axios.get(`${config.dev}/v2/choices/getAllItems/${choiceId}`, {
        headers: authHeaders(),
      });
      const nextItems = response.data?.items || [];
      setChoiceItemsMap((prev) => ({ ...prev, [choiceId]: nextItems }));
      return nextItems;
    } catch (error) {
      console.error("ProductEditing: choice items load failed", error);
      return [];
    }
  };

  const fetchItemsList = async () => {
    try {
      const itemsRes = await axios.get(`${config.dev}/v2/items/retrieve`, {
        headers: authHeaders(),
      });
      const fetchedItems = (itemsRes.data || []).filter((item) => !item.isArchived);
      setItems(fetchedItems);
    } catch (error) {
      console.error("ProductEditing: items load failed", error);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [productRes, catalog] = await Promise.all([
          axios.get(`${config.dev}/v2/products/retrieve/${id}?trackOpen=false`, {
            headers: authHeaders(),
          }),
          fetchChoices(),
        ]);
        if (cancelled) return;
        const product = productRes?.data;
        if (!product?._id) {
          setNotFound(true);
          return;
        }

        const details = {};
        const nextOpen = {};
        MENU_LANGUAGE_CODES.forEach((code) => {
          const langData = Array.isArray(product.productDetails)
            ? product.productDetails.find((d) => d.language === code)
            : null;
          details[code] = {
            productTitle: langData?.productTitle || "",
            productDescription: langData?.productDescription || "",
          };
          if (details[code].productTitle.trim() || details[code].productDescription.trim()) {
            nextOpen[code] = true;
          }
        });
        setProductDetails(details);
        setOpenLangs(nextOpen);
        setFormData({
          price: product.price ?? 0,
          secondaryPrice: product.secondaryPrice == null ? "" : product.secondaryPrice,
          isMultipleSize: !!product.haveMultipleSize,
          extraSizes:
            Array.isArray(product.extraSizes) && product.extraSizes.length
              ? product.extraSizes.map((size) => ({
                  size: size.size || "",
                  finalPrice: size.finalPrice ?? "",
                  secondaryFinalPrice: size.secondaryFinalPrice ?? "",
                }))
              : [{ size: "", finalPrice: "", secondaryFinalPrice: "" }],
        });
        setImageDisabled(Boolean(product.imageDisabled));
        setProductBadges(normalizeProductBadgesForSave(product.productBadges, product.productBadge || ""));
        setNutritionValues(normalizeNutritionValuesForSave(product.nutritionValues));
        setTags(Array.isArray(product.tags) ? product.tags : []);
        setSavedPhotos(Array.isArray(product.photos) ? product.photos.filter(Boolean) : []);
        setPhotoRemoved(false);
        setFile(null);
        setImageUrl(null);

        const assignedIds = (product.choices || [])
          .map((choice) => asId(choice))
          .filter(Boolean);
        const hiddenMap = {};
        (product.hiddenChoiceItems || []).forEach((row) => {
          const cid = asId(row?.choice);
          if (!cid) return;
          hiddenMap[cid] = (row.items || []).map((item) => asId(item)).filter(Boolean);
        });
        setHiddenByChoice(hiddenMap);
        setSelectedChoices(assignedIds);
        setChoicesOpen(assignedIds.length > 0);
        setOpenChoiceBodies({});

        const merged = [...catalog];
        (product.choices || []).forEach((choice) => {
          if (!choice || typeof choice !== "object" || !choice._id) return;
          if (!merged.some((c) => asId(c) === asId(choice))) merged.unshift(choice);
        });
        setChoices(merged);

        const kept = {};
        await Promise.all(
          assignedIds.map(async (choiceId) => {
            const choiceItems = await fetchChoiceItemsForChoice(choiceId);
            const allIds = choiceItems.map((item) => asId(item._id));
            const hidden = hiddenMap[choiceId] || [];
            kept[choiceId] = allIds.filter((itemId) => !hidden.includes(itemId));
          })
        );
        if (!cancelled) setSelectedItemsByChoice(kept);
      } catch (err) {
        console.error("ProductEditing: load failed", err);
        toast.error(t("errorTitle"));
        setNotFound(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setChoicesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  useEffect(() => {
    if (showAddItemModal) fetchItemsList();
  }, [showAddItemModal]);

  useEffect(() => {
    if (!choicesOpen) return;
    choices.forEach((choice) => {
      const choiceId = asId(choice._id);
      if (choiceId && !choiceItemsMap[choiceId]) fetchChoiceItemsForChoice(choiceId);
    });
  }, [choicesOpen, choices]);

  useEffect(() => {
    const filtered = items.filter((item) => {
      const title = itemTitle(item, "").toLowerCase();
      return title.includes(searchItemTerm.toLowerCase());
    });
    setfilteredItems(filtered);
  }, [searchItemTerm, items]);

  useEffect(() => {
    if (!openChoiceMenuId && !openItemMenuId) return undefined;
    const closeMenus = () => {
      setOpenChoiceMenuId(null);
      setOpenItemMenuId(null);
    };
    document.addEventListener("click", closeMenus);
    return () => document.removeEventListener("click", closeMenus);
  }, [openChoiceMenuId, openItemMenuId]);

  const query = searchTerm.trim().toLowerCase();
  const visibleChoices = choices.filter(
    (choice) => !query || choiceTitle(choice, "").toLowerCase().includes(query)
  );

  const itemIdsForChoice = (choiceId) =>
    (choiceItemsMap[choiceId] || []).map((item) => asId(item._id));

  const handleChoiceToggle = (choiceId) => {
    setSelectedChoices((prev) => {
      const isOn = prev.includes(choiceId);
      if (isOn) {
        setSelectedItemsByChoice((map) => {
          const next = { ...map };
          delete next[choiceId];
          return next;
        });
        return prev.filter((x) => x !== choiceId);
      }
      const itemIds = itemIdsForChoice(choiceId);
      const hidden = hiddenByChoice[choiceId] || [];
      if (itemIds.length) {
        setSelectedItemsByChoice((map) => ({
          ...map,
          [choiceId]: itemIds.filter((itemId) => !hidden.includes(itemId)),
        }));
      }
      if (!choiceItemsMap[choiceId]) fetchChoiceItemsForChoice(choiceId);
      return [...prev, choiceId];
    });
  };

  const handleChoiceItemToggle = (choiceId, itemId) => {
    const allIds = itemIdsForChoice(choiceId);
    setSelectedItemsByChoice((map) => {
      const current = map[choiceId] ?? allIds;
      const nextIds = current.includes(itemId)
        ? current.filter((x) => x !== itemId)
        : [...current, itemId];
      if (nextIds.length === 0) {
        setSelectedChoices((prev) => prev.filter((x) => x !== choiceId));
        const next = { ...map };
        delete next[choiceId];
        return next;
      }
      setSelectedChoices((prev) => (prev.includes(choiceId) ? prev : [...prev, choiceId]));
      return { ...map, [choiceId]: nextIds };
    });
  };

  const handleLangInputChange = (lang, field, value) => {
    setProductDetails((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  const handleAddTag = () => {
    const tag = currentTag.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setCurrentTag("");
    }
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    const { isValid, message } = validateImageFile(selected, 5);
    if (!isValid) {
      toast.error(message);
      return;
    }
    setFile(selected);
    setImageUrl(URL.createObjectURL(selected));
    setPhotoRemoved(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;

    const productDetailsToSend = Object.entries(productDetails)
      .map(([lang, details]) => ({
        language: lang,
        productTitle: (details.productTitle ?? "").trim(),
        productDescription: (details.productDescription ?? "").trim(),
      }))
      .filter((d) => d.productTitle || d.productDescription);

    if (!productDetailsToSend.some((d) => d.productTitle)) {
      swal(t("warningTitle"), t("productTitleRequired"), "warning");
      return;
    }

    const extraSizes = formData.isMultipleSize
      ? formData.extraSizes
          .filter(
            (size) =>
              size.size ||
              parseOptionalPrice(size.finalPrice) != null ||
              parseOptionalPrice(size.secondaryFinalPrice) != null
          )
          .map((size) => ({
            size: size.size,
            finalPrice: parseOptionalPrice(size.finalPrice) ?? 0,
            secondaryFinalPrice: parseOptionalPrice(size.secondaryFinalPrice),
          }))
      : [];

    setSubmitting(true);
    try {
      let mergedPhotos = photoRemoved ? [] : savedPhotos;
      if (file) {
        const photoData = new FormData();
        photoData.append("file", file);
        const photoRes = await axios.put(`${config.dev}/v2/products/photos/${id}`, photoData, {
          headers: authHeaders(),
        });
        mergedPhotos = photoRes.data?.photos ?? mergedPhotos;
        setSavedPhotos(mergedPhotos);
        setFile(null);
        setImageUrl(null);
        setPhotoRemoved(false);
      }

      await axios.put(
        `${config.dev}/v2/products/edit/${id}`,
        {
          productDetails: productDetailsToSend,
          price: parseOptionalPrice(formData.price) ?? 0,
          secondaryPrice: parseOptionalPrice(formData.secondaryPrice),
          haveMultipleSize: formData.isMultipleSize || false,
          extraSizes,
          nutritionValues: normalizeNutritionValuesForSave(nutritionValues),
          productBadges: normalizeProductBadgesForSave(productBadges),
          productBadge: "",
          photos: mergedPhotos,
          imageDisabled,
          tags,
        },
        { headers: authHeaders() }
      );

      try {
        const children = selectedChoices.map((choiceId) => ({
          id: choiceId,
          products: (choiceItemsMap[choiceId] || []).map((item) => ({ id: asId(item._id) })),
        }));
        await syncProductChoicesAndItems(
          id,
          selectedChoices,
          hiddenChoiceItemsFromKeep(children, selectedChoices, selectedItemsByChoice)
        );
      } catch (err) {
        console.error("ProductEditing: assign choices failed", err);
        toast.warn(t("prodFormUpdateAttachFailed"));
      }

      swal({
        title: t("successTitle"),
        text: t("productUpdated"),
        icon: "success",
        timer: 1000,
        buttons: false,
      });
      setTimeout(() => navigate(`/product-sheet/${id}`), 1000);
    } catch (err) {
      console.error("ProductEditing: save failed", err?.response?.data || err.message);
      swal(t("errorTitle"), err?.response?.data?.message || t("failedUpdateProduct"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openLanguages = languages.filter((lang) => isLangOpen(lang.code));
  const canSubmit = !submitting && !loading;

  const langFields = (lang) => (
    <div className="catform__lang-panel" key={lang.code} dir={lang.code === "ar" ? "rtl" : undefined}>
      <div className="catform__field">
        <label className="catform__label" htmlFor={`edit-prod-title-${lang.code}`}>
          <img src={lang.flag} alt="" />
          {lang.label} — {t("title")}
        </label>
        <input
          id={`edit-prod-title-${lang.code}`}
          type="text"
          className="catform__input"
          value={productDetails[lang.code]?.productTitle || ""}
          onChange={(e) => handleLangInputChange(lang.code, "productTitle", e.target.value)}
        />
      </div>
      <div className="catform__field">
        <label className="catform__label" htmlFor={`edit-prod-desc-${lang.code}`}>
          {t("description")}
        </label>
        <textarea
          id={`edit-prod-desc-${lang.code}`}
          className="catform__input catform__textarea"
          rows={3}
          value={productDetails[lang.code]?.productDescription || ""}
          onChange={(e) => handleLangInputChange(lang.code, "productDescription", e.target.value)}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("editProduct")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageTitle activeMenu={t("editProduct")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
          <div className="catform__actions">
            <button
              type="button"
              className="btn catform__btn catform__btn--cancel"
              onClick={() => navigate("/products")}
            >
              {t("productsList")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("editProduct")} motherMenu={t("kitchen")} />

      <form className="catform" onSubmit={handleSubmit} noValidate>
        <div className="catform__top">
          <h1 className="catform__title">{t("editProduct")}</h1>
          <button
            type="button"
            className="btn catform__link"
            onClick={() => navigate(`/product-sheet/${id}`)}
          >
            <i className="fas fa-arrow-left" aria-hidden /> {t("viewProductDetails")}
          </button>
        </div>

        <section className="catform__section">
          <h2 className="catform__heading">
            {t("prodFormPhoto")} <span className="catform__optional">{t("optional")}</span>
          </h2>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept={IMAGE_PICKER_ACCEPT}
            onChange={handleFileChange}
          />
          <div className="catform__photo">
            <button type="button" className="catform__thumb" onClick={() => fileInputRef.current?.click()}>
              {previewSrc ? <img src={previewSrc} alt="" /> : <i className="fas fa-camera" aria-hidden />}
            </button>
            <div className="catform__photo-actions">
              <button type="button" className="btn catform__small" onClick={() => fileInputRef.current?.click()}>
                {previewSrc ? t("catFormReplacePhoto") : t("catFormChoosePhoto")}
              </button>
              {previewSrc && (
                <button
                  type="button"
                  className="btn catform__small is-danger"
                  onClick={() => {
                    setFile(null);
                    setImageUrl(null);
                    setPhotoRemoved(true);
                  }}
                >
                  {t("catFormRemovePhoto")}
                </button>
              )}
              <label className={`catform__radio ${imageDisabled ? "is-on" : ""}`}>
                <input
                  type="checkbox"
                  checked={imageDisabled}
                  onChange={(e) => setImageDisabled(e.target.checked)}
                />
                {t("disableImage")}
              </label>
            </div>
          </div>
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">{t("catFormStepName")}</h2>
          <p className="catform__pills-label">{t("prodFormChooseLanguage")}</p>
          <div className="catform__pills">
            {languages.map((lang) => {
              const filled = Boolean(
                productDetails[lang.code]?.productTitle?.trim() ||
                  productDetails[lang.code]?.productDescription?.trim()
              );
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`catform__pill ${isLangOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                  aria-pressed={isLangOpen(lang.code)}
                  onClick={() => toggleLang(lang.code)}
                >
                  <img src={lang.flag} alt="" />
                  {lang.label}
                  {filled && <i className="fas fa-check" aria-hidden />}
                </button>
              );
            })}
          </div>
          <div className="catform__lang-fields">{openLanguages.map((lang) => langFields(lang))}</div>
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">{t("prodFormPrice")}</h2>
          <DualPriceFields
            price={formData.price}
            secondaryPrice={formData.secondaryPrice}
            disabled={formData.isMultipleSize}
            onPriceChange={(value) => setFormData({ ...formData, price: value })}
            onSecondaryPriceChange={(value) => setFormData({ ...formData, secondaryPrice: value })}
          />
          <label className={`catform__radio catform__radio--check ${formData.isMultipleSize ? "is-on" : ""}`}>
            <input
              type="checkbox"
              checked={formData.isMultipleSize}
              onChange={() =>
                setFormData({ ...formData, isMultipleSize: !formData.isMultipleSize })
              }
            />
            {t("haveMultipleSizes")}
          </label>
          {formData.isMultipleSize && (
            <div className="catform__sizes">
              {formData.extraSizes.map((size, index) => (
                <div key={index} className="catform__size-row">
                  <input
                    type="text"
                    className="catform__input"
                    placeholder={t("prodFormSize")}
                    value={size.size}
                    onChange={(e) => {
                      const next = [...formData.extraSizes];
                      next[index] = { ...next[index], size: e.target.value };
                      setFormData({ ...formData, extraSizes: next });
                    }}
                  />
                  <ExtraSizeDualPriceInputs
                    size={size}
                    onFinalPriceChange={(value) => {
                      const next = [...formData.extraSizes];
                      next[index] = { ...next[index], finalPrice: value };
                      setFormData({ ...formData, extraSizes: next });
                    }}
                    onSecondaryFinalPriceChange={(value) => {
                      const next = [...formData.extraSizes];
                      next[index] = { ...next[index], secondaryFinalPrice: value };
                      setFormData({ ...formData, extraSizes: next });
                    }}
                  />
                  <button
                    type="button"
                    className="btn catform__small is-danger"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        extraSizes: formData.extraSizes.filter((_, i) => i !== index),
                      })
                    }
                  >
                    <i className="fa fa-trash" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn catform__small"
                onClick={() =>
                  setFormData({
                    ...formData,
                    extraSizes: [
                      ...formData.extraSizes,
                      { size: "", finalPrice: 0, secondaryFinalPrice: "" },
                    ],
                  })
                }
              >
                <i className="fas fa-plus" aria-hidden /> {t("extraSizes")}
              </button>
            </div>
          )}
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">
            {t("prodFormDetails")} <span className="catform__optional">{t("optional")}</span>
          </h2>
          <ProductBadgesEditor values={productBadges} onChange={setProductBadges} />
          <NutritionValuesEditor values={nutritionValues} onChange={setNutritionValues} />
          <div className="prod-extra-tags">
            <div className="prod-extra-tags__head">
              <span className="prod-extra-trigger__icon" aria-hidden>
                <i className="fas fa-tags" />
              </span>
              <span className="prod-extra-tags__copy">
                <span className="prod-extra-tags__title">{t("tags") || "Tags"}</span>
                <span className="prod-extra-tags__hint">{t("prodFormTagsHint")}</span>
              </span>
            </div>
            <div className="catform__tag-row">
              <input
                type="text"
                className="catform__input"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                placeholder={t("prodFormTagPlaceholder")}
              />
              <button type="button" className="btn catform__small" onClick={handleAddTag}>
                {t("prodFormAddTag")}
              </button>
            </div>
            {tags.length > 0 && (
              <div className="catform__chips">
                {tags.map((tag) => (
                  <span key={tag} className="catform__chip">
                    {tag}
                    <button type="button" aria-label={t("remove")} onClick={() => setTags(tags.filter((x) => x !== tag))}>
                      <i className="fas fa-times" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className={`catform__section ${choicesOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="catform__fold"
            aria-expanded={choicesOpen}
            aria-controls="editprod-choices"
            onClick={() => setChoicesOpen((open) => !open)}
          >
            <h2 className="catform__heading">
              {t("prodFormChoices")} <span className="catform__optional">{t("optional")}</span>
              {selectedChoices.length > 0 && (
                <span className="catform__fold-count">{selectedChoices.length}</span>
              )}
            </h2>
            <i
              className={`fas fa-chevron-down catform__fold-arrow ${choicesOpen ? "is-open" : ""}`}
              aria-hidden
            />
          </button>
          <p className="catform__help">{t("choicesHint")}</p>

          {choicesOpen && (
            <div id="editprod-choices" className="catform__fold-body">
              <div className="prod-choices__toolbar">
                <div className="prod-choices__search">
                  <i className="fas fa-search prod-choices__search-icon" aria-hidden />
                  <input
                    type="text"
                    className="catform__input"
                    placeholder={`${t("searchChoice")}…`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      className="prod-choices__search-clear"
                      onClick={() => setSearchTerm("")}
                      aria-label={t("cancel")}
                    >
                      <i className="fas fa-times" />
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn catform__small"
                  onClick={() => setShowAddChoiceModal(true)}
                >
                  <i className="fas fa-plus" aria-hidden /> {t("addChoice")}
                </button>
              </div>

              <div className="prod-choices__list">
                {choicesLoading ? (
                  <div className="prod-choices__empty">{t("loading")}…</div>
                ) : visibleChoices.length === 0 ? (
                  <div className="prod-choices__empty">
                    <i className="fas fa-layer-group" aria-hidden />
                    <p>{searchTerm ? t("noChoicesFound") : t("choiceFormEmpty")}</p>
                    {!searchTerm && (
                      <button
                        type="button"
                        className="btn catform__small"
                        onClick={() => setShowAddChoiceModal(true)}
                      >
                        <i className="fas fa-plus" aria-hidden /> {t("addChoice")}
                      </button>
                    )}
                  </div>
                ) : (
                  visibleChoices.map((choice) => {
                    const choiceId = asId(choice._id);
                    const choiceItems = choiceItemsMap[choiceId] || [];
                    const isChecked = selectedChoices.includes(choiceId);
                    const keptItemIds =
                      selectedItemsByChoice[choiceId] ??
                      (isChecked ? choiceItems.map((item) => asId(item._id)) : []);
                    const bodyOpen = isChecked && (openChoiceBodies[choiceId] ?? false);
                    return (
                      <article
                        key={choiceId}
                        className={`prod-choice${isChecked ? " is-checked" : ""}${
                          bodyOpen ? " is-open" : ""
                        }`}
                      >
                        <div className="prod-choice__head">
                          <label className="prod-choice__check">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleChoiceToggle(choiceId)}
                            />
                            <span className="prod-choice__name">{choiceTitle(choice)}</span>
                          </label>
                          {choiceItems.length > 0 && (
                            <span className="prod-choice__count">
                              {isChecked
                                ? `${keptItemIds.length}/${choiceItems.length}`
                                : choiceItems.length}
                            </span>
                          )}
                          {isChecked && choiceItems.length > 0 && (
                            <button
                              type="button"
                              className={`btn menu-picker__chevron ${bodyOpen ? "is-open" : ""}`}
                              onClick={() =>
                                setOpenChoiceBodies((prev) => ({
                                  ...prev,
                                  [choiceId]: !(prev[choiceId] ?? false),
                                }))
                              }
                              aria-expanded={bodyOpen}
                            >
                              <i className="fas fa-chevron-down" aria-hidden />
                            </button>
                          )}
                          <div className="prod-choice__more">
                            <button
                              type="button"
                              className="prod-choice__icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenChoiceMenuId((prev) => (prev === choiceId ? null : choiceId));
                                setOpenItemMenuId(null);
                              }}
                              aria-label={t("editChoice")}
                            >
                              <FiMoreHorizontal size={16} />
                            </button>
                            {openChoiceMenuId === choiceId && (
                              <div className="prod-choice__menu" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenChoiceMenuId(null);
                                    setEditingChoice(choice);
                                    setShowEditChoiceModal(true);
                                  }}
                                >
                                  <FiEdit2 size={14} />
                                  {t("editChoice")}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {!bodyOpen && choiceItems.length > 0 && (
                          <div className="prod-choice__preview">
                            {choiceItems.slice(0, 3).map((item) => (
                              <span key={asId(item._id)} className="prod-choice__chip">
                                {itemTitle(item)}
                              </span>
                            ))}
                            {choiceItems.length > 3 && (
                              <span className="prod-choice__chip">+{choiceItems.length - 3}</span>
                            )}
                          </div>
                        )}

                        {bodyOpen && (
                          <div className="prod-choice__body">
                            {choiceItems.map((item) => {
                              const itemId = asId(item._id);
                              const itemOn = keptItemIds.includes(itemId);
                              return (
                                <div
                                  key={itemId}
                                  className={`prod-choice__item${itemOn ? " is-checked" : ""}`}
                                >
                                  <label className="prod-choice__check">
                                    <input
                                      type="checkbox"
                                      checked={itemOn}
                                      onChange={() => handleChoiceItemToggle(choiceId, itemId)}
                                    />
                                    <span className="prod-choice__item-name">{itemTitle(item)}</span>
                                  </label>
                                  <div className="prod-choice__more">
                                    <button
                                      type="button"
                                      className="prod-choice__icon-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenItemMenuId((prev) => (prev === itemId ? null : itemId));
                                        setOpenChoiceMenuId(null);
                                      }}
                                      aria-label={t("editItem")}
                                    >
                                      <FiMoreVertical size={16} />
                                    </button>
                                    {openItemMenuId === itemId && (
                                      <div
                                        className="prod-choice__menu"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setOpenItemMenuId(null);
                                            setEditingItem(item);
                                            setShowEditItemModal(true);
                                          }}
                                        >
                                          <FiEdit2 size={14} />
                                          {t("editItem")}
                                        </button>
                                        <button
                                          type="button"
                                          className="is-danger"
                                          onClick={async () => {
                                            setOpenItemMenuId(null);
                                            try {
                                              await removeItemFromChoice(choiceId, itemId);
                                              await fetchChoiceItemsForChoice(choiceId);
                                            } catch (error) {
                                              console.error(error);
                                            }
                                          }}
                                        >
                                          <FiTrash2 size={14} />
                                          {t("deleteItem")}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            <button
                              type="button"
                              className="prod-choice__assign"
                              onClick={() => setShowAddItemModal(choiceId)}
                            >
                              <FiPlus size={12} />
                              {t("assignItems")}
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </section>

        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate(`/product-sheet/${id}`)}
            disabled={submitting}
          >
            {t("cancel")}
          </button>
          <button type="submit" className="btn catform__btn catform__btn--primary" disabled={!canSubmit}>
            {submitting ? t("loading") : t("saveChanges")}
          </button>
        </div>
      </form>

      {showAddChoiceModal && (
        <div className="modal-overlay" onClick={() => setShowAddChoiceModal(false)}>
          <div className="modal-container prod-modal prod-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("addChoice")}</h5>
              <button className="btn-close" onClick={() => setShowAddChoiceModal(false)} />
            </div>
            <div className="modal-body">
              <AddChoice
                onSuccess={() => {
                  fetchChoices();
                  setShowAddChoiceModal(false);
                }}
                onClose={() => setShowAddChoiceModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showEditChoiceModal && (
        <div className="modal-overlay" onClick={() => setShowEditChoiceModal(false)}>
          <div className="modal-container prod-modal prod-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("editChoice")}</h5>
              <button className="btn-close" onClick={() => setShowEditChoiceModal(false)} />
            </div>
            <div className="modal-body">
              <EditChoice
                open={showEditChoiceModal}
                choiceId={editingChoice?._id}
                choice={editingChoice}
                onClose={() => setShowEditChoiceModal(false)}
                onUpdated={() => fetchChoices(searchTerm)}
              />
            </div>
          </div>
        </div>
      )}

      {showAddItemModal && (
        <div className="modal-overlay" onClick={() => setShowAddItemModal(false)}>
          <div className="modal-container prod-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("assignItems")}</h5>
              <button className="btn-close" onClick={() => setShowAddItemModal(false)} />
            </div>
            <div className="modal-body">
              <div className="prod-modal__tabs">
                <button
                  type="button"
                  className={`prod-modal__tab${activeSection === "existing" ? " is-active" : ""}`}
                  onClick={() => setActiveSection("existing")}
                >
                  {t("itemsList")} ({items.length})
                </button>
                <button
                  type="button"
                  className={`prod-modal__tab${activeSection === "new" ? " is-active" : ""}`}
                  onClick={() => setActiveSection("new")}
                >
                  {t("addItem")}
                </button>
              </div>

              {activeSection === "existing" && (
                <div>
                  {items.length === 0 ? (
                    <div className="prod-choices__empty">
                      <i className="fas fa-utensils" aria-hidden />
                      <p>{t("itemFormEmpty")}</p>
                      <button
                        type="button"
                        className="btn catform__small"
                        onClick={() => setActiveSection("new")}
                      >
                        <i className="fas fa-plus" aria-hidden /> {t("addItem")}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="prod-choices__search">
                        <i className="fas fa-search prod-choices__search-icon" aria-hidden />
                        <input
                          type="text"
                          className="catform__input"
                          placeholder={`${t("searchItem")}…`}
                          value={searchItemTerm}
                          onChange={(e) => setSearchItemTerm(e.target.value)}
                        />
                      </div>
                      <div className="prod-modal__pick">
                        {filteredItems
                          .filter(
                            (item) =>
                              !choiceItemsMap[showAddItemModal]?.some(
                                (assigned) => asId(assigned._id) === asId(item._id)
                              )
                          )
                          .map((item) => {
                            const isSelected = selectedItems.includes(item._id);
                            return (
                              <label key={item._id} className={`prod-modal__option${isSelected ? " is-on" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    setSelectedItems((prev) =>
                                      prev.includes(item._id)
                                        ? prev.filter((x) => x !== item._id)
                                        : [...prev, item._id]
                                    )
                                  }
                                />
                                {itemTitle(item)}
                              </label>
                            );
                          })}
                      </div>
                    </>
                  )}
                  {selectedItems.length > 0 && (
                    <div className="catform__actions" style={{ borderTop: "none", paddingTop: "0.9rem" }}>
                      <button
                        type="button"
                        className="btn catform__btn catform__btn--primary"
                        onClick={async () => {
                          try {
                            const choiceId = showAddItemModal;
                            await assignItemsToChoice(choiceId, selectedItems);
                            setSelectedChoices((prev) =>
                              prev.includes(choiceId) ? prev : [...prev, choiceId]
                            );
                            setSelectedItemsByChoice((map) => ({
                              ...map,
                              [choiceId]: Array.from(
                                new Set([...(map[choiceId] || []), ...selectedItems.map(asId)])
                              ),
                            }));
                            setSelectedItems([]);
                            await fetchChoiceItemsForChoice(choiceId);
                            setActiveSection("existing");
                            setShowAddItemModal(false);
                          } catch (error) {
                            console.error(error);
                          }
                        }}
                      >
                        {t("prodFormDone")}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeSection === "new" && (
                <AddItem
                  onSuccess={() => {
                    fetchItemsList();
                    setActiveSection("existing");
                  }}
                  onClose={() => setShowAddItemModal(false)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showEditItemModal && (
        <div className="modal-overlay" onClick={() => setShowEditItemModal(false)}>
          <div className="modal-container prod-modal prod-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("editItem")}</h5>
              <button className="btn-close" onClick={() => setShowEditItemModal(false)} />
            </div>
            <div className="modal-body">
              <EditItem
                open={showEditItemModal}
                itemId={editingItem?._id}
                item={editingItem}
                onClose={() => setShowEditItemModal(false)}
                onUpdated={async () => {
                  await fetchItemsList();
                  if (editingChoice?._id) await fetchChoiceItemsForChoice(editingChoice._id);
                  setShowEditItemModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductEditing;
